import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_MESSAGE_TTL_MS } from './constants';
import { Message, MessagesState } from './types';

const initialState: MessagesState = {
  messages: {},
  softDeletedMessages: {},
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    pushMessage(
      state,
      action: PayloadAction<{ key: string | number; message: Omit<Message, 'id' | 'ttl'> & { ttl?: number } }>
    ) {
      const { key, message } = action.payload;
      const { ttl: ttlOverride, ...rest } = message;
      const newMessage: Message = {
        ...rest,
        id: Date.now().toString(),
        ttl: Date.now() + (ttlOverride ?? DEFAULT_MESSAGE_TTL_MS),
      };

      if (!state.messages[key]) {
        state.messages[key] = [];
      }
      state.messages[key].push(newMessage);
    },

    clearMessages(state) {
      state.messages = {};
    },

    clearMessagesByKey(state, action: PayloadAction<string | number>) {
      delete state.messages[action.payload];
    },

    softDeleteMessagesByKey(state, action: PayloadAction<{ key: string | number; messages: Message[] }>) {
      const { key, messages } = action.payload;
      delete state.messages[key];
      if (messages.length > 0) {
        state.softDeletedMessages[key] = messages;
      }
    },

    restoreSoftDeletedMessagesByKey(state, action: PayloadAction<string | number>) {
      const key = action.payload;
      const snapshot = state.softDeletedMessages[key];
      if (snapshot && snapshot.length > 0) {
        state.messages[key] = snapshot;
      }
      delete state.softDeletedMessages[key];
    },

    clearSoftDeletedMessagesByKey(state, action: PayloadAction<string | number>) {
      delete state.softDeletedMessages[action.payload];
    },

    removeMessageById(state, action: PayloadAction<{ key: string | number; messageId: string }>) {
      const { key, messageId } = action.payload;
      if (state.messages[key]) {
        state.messages[key] = state.messages[key].filter((message) => message.id !== messageId);
        if (state.messages[key].length === 0) {
          delete state.messages[key];
        }
      }
    },

    cleanExpiredMessages(state) {
      const now = Date.now();
      const keys = Object.keys(state.messages);
      for (const key of keys) {
        state.messages[key] = state.messages[key].filter((message) => message.ttl > now);
        if (state.messages[key].length === 0) {
          delete state.messages[key];
        }
      }
    },
  },
});

// Selectors
export const selectAllMessages = (state: { messagesBus: MessagesState }) => state.messagesBus.messages;

// Empty array constant to avoid creating new references
const EMPTY_MESSAGES: Message[] = [];

// Direct selector for when you need to pass the key as a parameter
export const selectMessagesByKey = (state: { messagesBus: MessagesState }, key: string | number) => state.messagesBus.messages[key] || EMPTY_MESSAGES;

export const selectMessagesFlat = (state: { messagesBus: MessagesState }) => {
  const allMessages: Message[] = [];
  Object.values(state.messagesBus.messages).forEach((messages) => {
    allMessages.push(...messages);
  });
  return allMessages;
};

export const {
  pushMessage,
  clearMessages,
  clearMessagesByKey,
  softDeleteMessagesByKey,
  restoreSoftDeletedMessagesByKey,
  clearSoftDeletedMessagesByKey,
  removeMessageById,
  cleanExpiredMessages,
} = messagesSlice.actions;
export default messagesSlice.reducer;
